(() => {
  const RESTRICTED_TYPES = ["Fabric Swatch", "Finish Sample"];
  const MAX_PER_VARIANT = 1;

  // Mini cart qty input ids are: CartBlocks-Form-Quantity-${item.key}
  const restrictedMiniCartInputIds = new Set();

  let running = false;
  let timer = null;
  let patched = false;

  const normalize = (s) => (s || "").trim().toLowerCase();
  const isRestricted = (type) =>
    RESTRICTED_TYPES.some((t) => normalize(t) === normalize(type));

  async function fetchCart() {
    const res = await fetch("/cart.js", { credentials: "same-origin" });
    if (!res.ok) throw new Error("cart.js failed");
    return res.json();
  }

  async function changeLineQuantity(lineKey, qty) {
    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id: lineKey, quantity: qty }),
    });
    if (!res.ok) throw new Error("change.js failed");
    return res.json();
  }

  // ---------------------------
  // Mini cart DOM helpers (guaranteed inside drawer)
  // ---------------------------
  function getMiniCartAside() {
    // The most reliable anchor from your HTML: form#MiniCart-Form contains the <aside ...>
    return (
      document.querySelector("form#MiniCart-Form aside") ||
      document.querySelector("form#MiniCart-Form")?.querySelector("aside") ||
      null
    );
  }

  function getMiniCartLineItemsContainer() {
    const aside = getMiniCartAside();
    if (!aside) return null;

    // Your line items live inside: <div class="divide-y divide-scheme-border"> ... <article ...>
    return (
      aside.querySelector("div.divide-y.divide-scheme-border") ||
      aside.querySelector(".divide-y.divide-scheme-border") ||
      aside
    );
  }

  // ---------------------------
  // Persistent message (MUST stay inside mini cart)
  // ---------------------------
  function ensureMessageEl() {
    const aside = getMiniCartAside();
    const parent = getMiniCartLineItemsContainer();
    if (!aside || !parent) return null;

    let el = aside.querySelector("#sample-unique-sku-error-global");
    if (!el) {
      el = document.createElement("div");
      el.id = "sample-unique-sku-error-global";
      el.style.display = "none";
      el.style.marginTop = "12px";
      el.innerHTML =
        '<p style="margin:0; padding:12px; border:1px solid #d72c0d; border-radius:8px;"></p>';

      // Append below items (last element inside items container)
      parent.appendChild(el);
    } else {
      // If the drawer re-renders, ensure it remains inside the current drawer
      if (!aside.contains(el)) parent.appendChild(el);
      else if (el.parentElement !== parent) parent.appendChild(el);
    }

    return el;
  }

  function showMessagePersistent(text) {
    const el = ensureMessageEl();
    if (!el) return;

    const p = el.querySelector("p");
    if (p) p.textContent = text || "";
    el.style.display = "block"; // persistent (no auto-hide)
  }

  function hideMessage() {
    const aside = getMiniCartAside();
    if (!aside) return;
    const el = aside.querySelector("#sample-unique-sku-error-global");
    if (el) el.style.display = "none";
  }

  function buildMessage(cart) {
    const items = (cart && cart.items) || [];
    const restrictedItems = items.filter((it) => isRestricted(it.product_type));
    if (!restrictedItems.length) return "";

    const totalItems = items.length;
    const restrictedCount = restrictedItems.length;
    const onlyRestricted = totalItems > 0 && restrictedCount === totalItems;

    const typesPresent = new Set(restrictedItems.map((it) => normalize(it.product_type)));
    const hasFabric = typesPresent.has(normalize("Fabric Swatch"));
    const hasFinish = typesPresent.has(normalize("Finish Sample"));
    const label =
      hasFabric && hasFinish
        ? "Fabric Swatches and Finish Samples"
        : hasFabric
          ? "Fabric Swatches"
          : "Finish Samples";

    const hadViolation = restrictedItems.some((it) => (it.quantity || 0) > 1);

    if (onlyRestricted) {
      return hadViolation
        ? `We adjusted your cart: ${label} are limited to one of each item per order.`
        : `${label} are limited to one of each item per order.`;
    }

    return hadViolation
      ? `Note: We adjusted your cart. ${label} are limited to one of each item per order.`
      : `Note: ${label} are limited to one of each item per order.`;
  }

  // ---------------------------
  // Mini cart input IDs
  // ---------------------------
  function indexRestrictedMiniCartInputs(cart) {
    restrictedMiniCartInputIds.clear();
    (cart?.items || []).forEach((item) => {
      if (!isRestricted(item.product_type)) return;
      restrictedMiniCartInputIds.add(`CartBlocks-Form-Quantity-${item.key}`);
    });
  }

  // Clamp any attempts to set >1 into these inputs (Vue/reactive renders included)
  function installInputValueClampOnce() {
    if (patched) return;
    patched = true;

    const proto = HTMLInputElement.prototype;
    const desc = Object.getOwnPropertyDescriptor(proto, "value");
    if (!desc?.set || !desc?.get) return;

    const originalSet = desc.set;
    const originalGet = desc.get;

    Object.defineProperty(proto, "value", {
      configurable: true,
      enumerable: desc.enumerable,
      get() {
        return originalGet.call(this);
      },
      set(v) {
        try {
          if (this?.id && restrictedMiniCartInputIds.has(this.id)) {
            const n = parseInt(v, 10);
            if (Number.isFinite(n) && n > 1) {
              return originalSet.call(this, "1");
            }
          }
        } catch (_) {}
        return originalSet.call(this, v);
      },
    });
  }

  function forceVisibleMiniCartQtyToOne() {
    restrictedMiniCartInputIds.forEach((id) => {
      const input = document.getElementById(id);
      if (!input) return;

      input.value = "1";
      input.setAttribute("value", "1");

      try {
        input.dispatchEvent(new Event("input", { bubbles: true }));
        input.dispatchEvent(new Event("change", { bubbles: true }));
      } catch (_) {}

      input.readOnly = true;
      input.setAttribute("readonly", "readonly");
      input.style.pointerEvents = "none";
    });
  }

  // ---------------------------
  // Enforcement
  // ---------------------------
  async function enforce() {
    if (running) return;
    running = true;

    try {
      const cart = await fetchCart();
      const items = cart?.items || [];

      indexRestrictedMiniCartInputs(cart);
      installInputValueClampOnce();

      const hasRestricted = items.some((it) => isRestricted(it.product_type));

      // ONLY show message if cart contains swatch/sample, and ONLY inside mini cart
      if (hasRestricted) {
        // Make sure the mini cart exists before trying to inject message.
        // If it isn't open yet, we'll try again on the next mutation/fetch.
        if (getMiniCartAside()) {
          showMessagePersistent(buildMessage(cart));
        }
      } else {
        hideMessage();
      }

      // Fix any violations in Shopify truth
      const violations = items.filter(
        (it) => isRestricted(it.product_type) && (it.quantity || 0) > MAX_PER_VARIANT
      );

      if (violations.length) {
        // Immediately clamp what user sees
        forceVisibleMiniCartQtyToOne();

        for (const it of violations) {
          await changeLineQuantity(it.key, MAX_PER_VARIANT);
        }

        // Re-fetch + re-apply clamp + message (drawer may re-render)
        const updated = await fetchCart();
        indexRestrictedMiniCartInputs(updated);
        forceVisibleMiniCartQtyToOne();

        const updatedHasRestricted = (updated?.items || []).some((it) =>
          isRestricted(it.product_type)
        );
        if (updatedHasRestricted && getMiniCartAside()) {
          showMessagePersistent(buildMessage(updated));
        } else {
          hideMessage();
        }
      } else {
        if (hasRestricted) forceVisibleMiniCartQtyToOne();
      }
    } catch (_) {
      // fail open
    } finally {
      running = false;
    }
  }

  function schedule(ms = 120) {
    clearTimeout(timer);
    timer = setTimeout(enforce, ms);
  }

  document.addEventListener("DOMContentLoaded", () => schedule(0));

  // Re-run on likely cart interactions
  document.addEventListener("click", (e) => {
    const el = e.target?.closest?.("button,a");
    if (!el) return;

    if (
      el.matches('[name="add"], [data-add-to-cart], .add-to-cart, button[type="submit"]') ||
      el.querySelector(".icon-plus") ||
      el.querySelector(".icon-minus")
    ) {
      schedule(80);
    }
  });

  // Patch fetch so AJAX cart calls trigger enforcement quickly
  const _fetch = window.fetch;
  window.fetch = async function (...args) {
    const res = await _fetch.apply(this, args);
    try {
      const url = String(args[0] || "");
      if (url.includes("/cart/add") || url.includes("/cart/change") || url.includes("/cart/update")) {
        schedule(80);
      }
    } catch (_) {}
    return res;
  };

  // If drawer re-renders, re-enforce and re-append message under items
  new MutationObserver(() => schedule(80)).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  window.enforceSampleLimits = enforce;
})();
